import { isAdFree, onAdFreeChange, refreshAdFree, removeAdsReady } from "./removeAds";
import { appAdsEnabled } from "./ads";
import { isEmbedded } from "./portal";

// AdMob ads for the iOS/Android app.
//
// The web game runs unchanged inside the app (Capacitor). When it detects it is running
// natively, it uses AdMob (Google's app ad network) instead of the website's AdSense ads,
// which Google does not allow inside apps.
//
// Interstitials (and the banner) are behind APP_ADS_ENABLED in ads.ts, separate from the
// website's WEBSITE_ADSENSE_ENABLED: switching the apps' ads on never puts AdSense on the site.
// ensureAdMob() is shared with the rewarded ad (rewardedAd.ts), which has its own switch
// (REWARDED_ENABLED), so it is not behind APP_ADS_ENABLED.
//
// Rafiki's real AdMob ad unit IDs (account pub-8481626500059505).


const AD_UNITS = {
  android: {
    banner: "ca-app-pub-8481626500059505/5589922267",
    interstitial: "ca-app-pub-8481626500059505/1974110889",
  },
  ios: {
    banner: "ca-app-pub-8481626500059505/7165185823",
    interstitial: "ca-app-pub-8481626500059505/7466110713",
  },
};
const USE_TEST_ADS = false; // real ads. Never tap your own ads: AdMob can suspend the account.

// Show a full-screen ad after every Nth finished round, never more than once every few minutes.
const INTERSTITIAL_EVERY_N_ROUNDS = 2;
const INTERSTITIAL_MIN_GAP_MS = 3 * 60 * 1000;
// iPhone players get a lighter ad load (see the monetisation plan, 31 Oct 2026).
const INTERSTITIAL_MIN_GAP_MS_IOS = 5 * 60 * 1000;
// No banner under the table: banners earn little and crowd the cards. Interstitials only.
const SHOW_BANNER = false;

declare global {
  interface Window {
    Capacitor?: {
      isNativePlatform: () => boolean;
      getPlatform: () => "ios" | "android" | "web";
      Plugins: { [name: string]: any };
    };
  }
}

export const isNativeApp = (): boolean =>
  typeof window !== "undefined" && Boolean(window.Capacitor?.isNativePlatform?.());

// AdMob interstitials/banner may run: the flag is on, this is the iPhone/Android app, and not a
// portal or Discord build (those only ever use their own SDK's ads).
const appAdsAllowed = (): boolean => appAdsEnabled() && !isEmbedded && isNativeApp();

const platform = (): "ios" | "android" => (window.Capacitor?.getPlatform() === "ios" ? "ios" : "android");
const admob = () => window.Capacitor?.Plugins?.AdMob;

let initialised = false;
let roundsSinceAd = 0;
let lastInterstitial = 0;
let admobReady: Promise<boolean> | null = null;

// Start the AdMob SDK once (with Google's consent form where required). Shared by the
// interstitials here and the optional rewarded ad (rewardedAd.ts). Resolves false if the SDK
// can't start, e.g. on an Amazon Fire tablet without Google Play services: ads then stay off
// and the game carries on.
export const ensureAdMob = (): Promise<boolean> => {
  if (isEmbedded || !isNativeApp() || !admob()) return Promise.resolve(false);
  if (admobReady) return admobReady;
  admobReady = (async () => {
    try {
      // The apps are rated 12+, so only ads suitable for teens.
      await admob().initialize({ initializeForTesting: USE_TEST_ADS, maxAdContentRating: "Teen" });
      // Google's consent form (certified CMP) for UK/EEA users, shown only where required.
      const consent = await admob().requestConsentInfo();
      if (consent?.isConsentFormAvailable && consent?.status === "REQUIRED") {
        await admob().showConsentForm();
      }
      if (platform() === "ios") {
        await admob().requestTrackingAuthorization?.();
      }
      return true;
    } catch (e) {
      // Ads must never break the game.
      console.warn("AdMob init failed", e);
      return false;
    }
  })();
  return admobReady;
};

export const initNativeAds = async () => {
  if (!appAdsAllowed() || initialised || !admob()) return;
  initialised = true;
  try {
    if (!(await ensureAdMob())) return;
    if (await removeAdsReady()) {
      await refreshAdFree();
      onAdFreeChange((adFree) => {
        if (adFree) hideAllAds();
      });
    }
    if (SHOW_BANNER) await showBanner();
  } catch (e) {
    // Ads must never break the game.
    console.warn("AdMob init failed", e);
  }
};

// Called after a "Remove ads" purchase: take the banner down straight away.
const hideAllAds = async () => {
  try {
    await admob().removeBanner?.();
  } catch (e) {
    // already gone
  }
  document.body.style.paddingBottom = "";
};

export const showBanner = async () => {
  if (!appAdsAllowed() || !admob() || isAdFree()) return;
  try {
    // Keep the game clear of the banner.
    admob().addListener?.("bannerAdSizeChanged", (size: { height: number }) => {
      document.body.style.paddingBottom = size?.height ? `${size.height}px` : "";
    });
    await admob().showBanner({
      adId: AD_UNITS[platform()].banner,
      adSize: "ADAPTIVE_BANNER",
      position: "BOTTOM_CENTER",
      margin: 0,
      isTesting: USE_TEST_ADS,
    });
  } catch (e) {
    console.warn("AdMob banner failed", e);
  }
};

// Called when a round finishes.
export const onRoundFinished = async () => {
  if (!appAdsAllowed() || !admob() || isAdFree()) return;
  roundsSinceAd += 1;
  const now = Date.now();
  if (roundsSinceAd < INTERSTITIAL_EVERY_N_ROUNDS || now - lastInterstitial < (platform() === "ios" ? INTERSTITIAL_MIN_GAP_MS_IOS : INTERSTITIAL_MIN_GAP_MS)) return;
  try {
    await admob().prepareInterstitial({ adId: AD_UNITS[platform()].interstitial, isTesting: USE_TEST_ADS });
    await admob().showInterstitial();
    roundsSinceAd = 0;
    lastInterstitial = now;
  } catch (e) {
    console.warn("AdMob interstitial failed", e);
  }
};
